import React, { useEffect, useMemo, useState } from "react";
import {
    Bell, Mail, MessageSquare, Webhook, Send, CheckCircle2,
    XCircle, Shield, Inbox, Zap, Activity, AlertTriangle,
} from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Notifications — route alerts and job-completion events to email, Slack,
 * Teams, or custom webhooks. Redesigned as a standalone page: a KPI strip
 * across the top, a three-column "channel card" grid, a collapsible
 * delivery rules panel, and a full-height delivery log on the right.
 */
export default function Notifications() {
    const { user } = useAuth();
    // Tenant admins AND platform superadmins can both edit notification
    // routing. Backend `require_roles("admin")` already lets superadmin
    // through by design (auth.py role hierarchy), so the frontend must
    // mirror that bypass — otherwise a superadmin lands on a read-only
    // page where every input is greyed-out and the Save / Send-test
    // buttons are entirely hidden.
    const canEdit = user?.role === "admin" || user?.role === "superadmin";
    const [settings, setSettings] = useState(null);
    const [log, setLog] = useState([]);
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [recipientsText, setRecipientsText] = useState("");

    const load = async () => {
        const [s, l] = await Promise.all([
            http.get("/settings/notifications"),
            http.get("/settings/notifications/log"),
        ]);
        setSettings(s.data);
        setRecipientsText((s.data.email_recipients || []).join(", "));
        setLog(l.data);
    };

    useEffect(() => {
        load().catch((e) => setErr(errMsg(e)));
    }, []);

    const stats = useMemo(() => {
        const total = log.length;
        const ok = log.filter((e) => e.ok).length;
        const fail = total - ok;
        const last = log[0]?.created_at;
        return { total, ok, fail, rate: total ? ((ok / total) * 100).toFixed(1) : "—", last };
    }, [log]);

    const activeChannels = useMemo(() => {
        if (!settings) return 0;
        let n = 0;
        if (settings.email_enabled && settings.email_recipients?.length) n++;
        if (settings.slack_webhook_url) n++;
        if (settings.teams_webhook_url) n++;
        if (settings.generic_webhook_url) n++;
        return n;
    }, [settings]);

    const save = async () => {
        setSaving(true);
        setErr("");
        try {
            const recipients = recipientsText
                .split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
            const payload = {
                email_enabled: settings.email_enabled,
                email_recipients: recipients,
                slack_webhook_url: settings.slack_webhook_url || "",
                teams_webhook_url: settings.teams_webhook_url || "",
                generic_webhook_url: settings.generic_webhook_url || "",
                min_severity: settings.min_severity || "warning",
                on_job_complete: !!settings.on_job_complete,
                on_alert: !!settings.on_alert,
                on_job_succeeded: settings.on_job_succeeded !== false,
                on_job_failed: settings.on_job_failed !== false,
                on_job_canceled: settings.on_job_canceled !== false,
                min_job_bytes: Math.max(0, parseInt(settings.min_job_bytes || 0, 10)),
            };
            await http.put("/settings/notifications", payload);
            await load();
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setSaving(false);
        }
    };

    const sendTest = async () => {
        setTesting(true);
        try {
            const { data } = await http.post("/settings/notifications/test");
            setLog(data.log || []);
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setTesting(false);
        }
    };

    if (!settings) {
        return <div className="p-6 font-mono text-xs uppercase tracking-widest text-muted-foreground">Loading…</div>;
    }

    return (
        <div className="mx-auto max-w-[1600px] px-6 py-6" data-testid="notifications-page">
            {/* Header */}
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                        Fan-out · Alerts routing
                    </div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight">Notifications</h1>
                    <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground">
                        Route alerts and job completions to email, Slack, Microsoft Teams, or any custom webhook.
                        Every attempt is logged for auditability.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {canEdit && (
                        <button
                            onClick={sendTest}
                            disabled={testing}
                            data-testid="notifications-test-btn"
                            className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-xs font-semibold uppercase tracking-widest transition hover:border-[#1D4ED8] hover:text-[#1D4ED8] disabled:opacity-60"
                        >
                            <Send size={13} /> {testing ? "Sending…" : "Send test"}
                        </button>
                    )}
                    {canEdit && (
                        <button
                            onClick={save}
                            disabled={saving}
                            data-testid="notifications-save-btn"
                            className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#1D4ED8] to-[#1E40AF] px-4 py-2 text-sm font-semibold text-white shadow transition hover:brightness-110 disabled:opacity-60"
                        >
                            {saving ? "Saving…" : "Save routing"}
                        </button>
                    )}
                </div>
            </div>

            {/* KPI strip */}
            <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="notifications-kpi">
                <Kpi label="Active channels" value={activeChannels} icon={Zap} color="#1D4ED8"
                    hint={activeChannels === 0 ? "None configured" : `of 4 possible`} />
                <Kpi label="Delivered" value={stats.ok} icon={CheckCircle2} color="#10b981" hint="last 100 events" />
                <Kpi label="Failed" value={stats.fail} icon={XCircle} color="#ef4444" hint={`${stats.rate}% success rate`} />
                <Kpi label="Last attempt" value={stats.last ? timeAgo(stats.last) : "—"} icon={Activity} color="#f59e0b"
                    hint={stats.last ? new Date(stats.last).toLocaleString() : "No deliveries yet"} />
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                {/* Left 2/3: channels + rules */}
                <div className="space-y-5 lg:col-span-2">
                    {/* Rules card */}
                    <Card icon={Bell} title="Delivery rules" subtitle="Which events fire notifications">
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <RuleToggle
                                icon={AlertTriangle}
                                label="Notify on alert"
                                hint="Anomaly detection, integrity failures."
                                value={!!settings.on_alert}
                                onChange={(v) => setSettings({ ...settings, on_alert: v })}
                                disabled={!canEdit}
                                testId="notif-toggle-on-alert"
                            />
                            <RuleToggle
                                icon={CheckCircle2}
                                label="Job lifecycle (master)"
                                hint="Umbrella switch — disables all per-event toggles below."
                                value={!!settings.on_job_complete}
                                onChange={(v) => setSettings({ ...settings, on_job_complete: v })}
                                disabled={!canEdit}
                                testId="notif-toggle-on-complete"
                            />
                        </div>

                        {/* Per-lifecycle granular gating — only meaningful
                            when the master "Job lifecycle" toggle is on. */}
                        <div className="mt-4 rounded-lg border border-border bg-background/30 p-3" data-testid="notif-lifecycle-group">
                            <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                Per-event channel rules
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                <RuleToggle
                                    icon={CheckCircle2}
                                    label="Succeeded"
                                    hint="Fires on succeeded or completed-with-errors."
                                    value={settings.on_job_succeeded !== false}
                                    onChange={(v) => setSettings({ ...settings, on_job_succeeded: v })}
                                    disabled={!canEdit || !settings.on_job_complete}
                                    testId="notif-toggle-on-succeeded"
                                />
                                <RuleToggle
                                    icon={XCircle}
                                    label="Failed"
                                    hint="Page someone — these usually need eyes."
                                    value={settings.on_job_failed !== false}
                                    onChange={(v) => setSettings({ ...settings, on_job_failed: v })}
                                    disabled={!canEdit || !settings.on_job_complete}
                                    testId="notif-toggle-on-failed"
                                />
                                <RuleToggle
                                    icon={AlertTriangle}
                                    label="Canceled"
                                    hint="User-initiated cancellations."
                                    value={settings.on_job_canceled !== false}
                                    onChange={(v) => setSettings({ ...settings, on_job_canceled: v })}
                                    disabled={!canEdit || !settings.on_job_complete}
                                    testId="notif-toggle-on-canceled"
                                />
                            </div>

                            <div className="mt-3">
                                <label className="block">
                                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                        Minimum job size (bytes) — don&apos;t fire below this
                                    </span>
                                    <input
                                        type="number"
                                        min={0}
                                        value={settings.min_job_bytes || 0}
                                        onChange={(e) => setSettings({ ...settings, min_job_bytes: e.target.value })}
                                        disabled={!canEdit || !settings.on_job_complete}
                                        data-testid="notif-min-job-bytes"
                                        className="w-44 rounded-md border border-input bg-background px-3 py-2 font-mono text-sm tabular-nums disabled:opacity-50"
                                    />
                                    <span className="ml-2 text-[10px] text-muted-foreground">0 = no threshold</span>
                                </label>
                            </div>
                        </div>
                        <div className="mt-4">
                            <label className="block">
                                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Minimum severity
                                </span>
                                <div className="flex gap-1.5">
                                    {["info", "warning", "error"].map((lvl) => (
                                        <button
                                            key={lvl}
                                            type="button"
                                            onClick={() => canEdit && setSettings({ ...settings, min_severity: lvl })}
                                            disabled={!canEdit}
                                            data-testid={`notif-severity-${lvl}`}
                                            className={[
                                                "rounded-md border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition",
                                                settings.min_severity === lvl
                                                    ? "border-[#1D4ED8] bg-[#1D4ED8]/10 text-[#1D4ED8]"
                                                    : "border-border bg-background text-muted-foreground hover:border-foreground/30",
                                            ].join(" ")}
                                        >
                                            {lvl === "info" && "Info"}
                                            {lvl === "warning" && "Warning+"}
                                            {lvl === "error" && "Error only"}
                                        </button>
                                    ))}
                                </div>
                            </label>
                        </div>
                    </Card>

                    {/* Channel cards grid */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ChannelCard
                            icon={Mail}
                            title="Email"
                            accent="#3b82f6"
                            status={settings.email_enabled && (settings.email_recipients?.length || 0) > 0 ? "active" : "idle"}
                            badge={settings.smtp_configured ? "SMTP live" : "SMTP mocked"}
                            badgeOk={settings.smtp_configured}
                            testId="notif-channel-email"
                        >
                            {!settings.smtp_configured && (
                                <div className="mb-2.5 rounded-md border border-[#f59e0b]/40 bg-[#f59e0b]/5 p-2 text-[11px] leading-snug text-[#f59e0b]">
                                    SMTP not configured. Set <code className="font-mono">SMTP_*</code> vars in
                                    <code className="font-mono"> backend/.env</code> to enable real delivery.
                                </div>
                            )}
                            <label className="mb-3 flex items-center justify-between gap-3">
                                <span className="text-sm">Enable email delivery</span>
                                <Switch value={!!settings.email_enabled}
                                    onChange={(v) => setSettings({ ...settings, email_enabled: v })}
                                    disabled={!canEdit} testId="notif-toggle-email" />
                            </label>
                            <InputField
                                label="Recipients (comma-separated)"
                                value={recipientsText}
                                onChange={setRecipientsText}
                                placeholder="ops@acme.com, security@acme.com"
                                disabled={!canEdit}
                                testId="notif-email-recipients"
                            />
                        </ChannelCard>

                        <ChannelCard
                            icon={MessageSquare}
                            title="Slack"
                            accent="#4a154b"
                            status={settings.slack_webhook_url ? "active" : "idle"}
                            testId="notif-channel-slack"
                        >
                            <InputField
                                label="Incoming webhook URL"
                                value={settings.slack_webhook_url || ""}
                                onChange={(v) => setSettings({ ...settings, slack_webhook_url: v })}
                                placeholder="https://hooks.slack.com/services/..."
                                disabled={!canEdit}
                                testId="notif-slack-url"
                            />
                        </ChannelCard>

                        <ChannelCard
                            icon={MessageSquare}
                            title="Microsoft Teams"
                            accent="#6264a7"
                            status={settings.teams_webhook_url ? "active" : "idle"}
                            testId="notif-channel-teams"
                        >
                            <InputField
                                label="Incoming connector URL"
                                value={settings.teams_webhook_url || ""}
                                onChange={(v) => setSettings({ ...settings, teams_webhook_url: v })}
                                placeholder="https://outlook.office.com/webhook/..."
                                disabled={!canEdit}
                                testId="notif-teams-url"
                            />
                        </ChannelCard>

                        <ChannelCard
                            icon={Webhook}
                            title="Generic JSON webhook"
                            accent="#10b981"
                            status={settings.generic_webhook_url ? "active" : "idle"}
                            testId="notif-channel-generic"
                        >
                            <InputField
                                label="HTTPS endpoint (POST JSON)"
                                value={settings.generic_webhook_url || ""}
                                onChange={(v) => setSettings({ ...settings, generic_webhook_url: v })}
                                placeholder="https://your-siem.example.com/moovbytes"
                                disabled={!canEdit}
                                testId="notif-generic-url"
                            />
                        </ChannelCard>
                    </div>

                    {err && (
                        <div className="rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5 px-3 py-2 text-xs text-[hsl(var(--destructive))]">
                            {err}
                        </div>
                    )}
                </div>

                {/* Right 1/3: delivery log */}
                <div className="sticky top-6 h-fit overflow-hidden rounded-xl border border-border bg-surface">
                    <div className="flex items-center justify-between border-b border-border px-4 py-3">
                        <div className="flex items-center gap-2">
                            <Inbox size={14} className="text-muted-foreground" />
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Delivery log
                                </div>
                                <div className="font-display text-sm font-bold">Last 100 attempts</div>
                            </div>
                        </div>
                        <Shield size={14} className="text-muted-foreground" />
                    </div>
                    <div className="scroll-thin max-h-[780px] overflow-y-auto divide-y divide-border">
                        {log.length === 0 && (
                            <div className="p-8 text-center text-xs text-muted-foreground">
                                <Send size={24} className="mx-auto mb-2 opacity-30" />
                                <div>No deliveries yet.</div>
                                <div className="mt-1">Configure a channel and click <span className="font-semibold">Send test</span>.</div>
                            </div>
                        )}
                        {log.map((e) => (
                            <LogRow key={e.id} entry={e} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ----- Sub-components -----

function Kpi({ label, value, icon: Icon, color, hint }) {
    return (
        <div className="rounded-xl border border-border bg-surface p-4">
            <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md" style={{ background: `${color}18`, color }}>
                    <Icon size={14} />
                </div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
            </div>
            <div className="mt-2 font-display text-2xl font-bold tracking-tight">{value}</div>
            {hint && <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>}
        </div>
    );
}

function Card({ icon: Icon, title, subtitle, children }) {
    return (
        <div className="rounded-xl border border-border bg-surface">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <Icon size={14} className="text-muted-foreground" />
                <div>
                    <div className="font-display text-base font-bold">{title}</div>
                    {subtitle && <div className="text-[11px] text-muted-foreground">{subtitle}</div>}
                </div>
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

function ChannelCard({ icon: Icon, title, accent, status, badge, badgeOk, children, testId }) {
    const live = status === "active";
    return (
        <div className="flex flex-col rounded-xl border border-border bg-surface" data-testid={testId}>
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md"
                        style={{ background: `${accent}18`, color: accent }}>
                        <Icon size={15} />
                    </div>
                    <div>
                        <div className="font-display text-sm font-bold leading-tight">{title}</div>
                        <div className={["mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest",
                            live ? "text-[#10b981]" : "text-muted-foreground"].join(" ")}>
                            <span className={["h-1.5 w-1.5 rounded-full",
                                live ? "bg-[#10b981]" : "bg-muted-foreground/40"].join(" ")} />
                            {live ? "Active" : "Not configured"}
                        </div>
                    </div>
                </div>
                {badge && (
                    <span className={["rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                        badgeOk ? "border-[#10b981]/40 bg-[#10b981]/5 text-[#10b981]"
                            : "border-[#f59e0b]/40 bg-[#f59e0b]/5 text-[#f59e0b]"].join(" ")}>
                        {badge}
                    </span>
                )}
            </div>
            <div className="flex-1 p-4">{children}</div>
        </div>
    );
}

function RuleToggle({ icon: Icon, label, hint, value, onChange, disabled, testId }) {
    return (
        <div className={["rounded-lg border border-border p-3 transition",
            value ? "border-[#1D4ED8]/40 bg-[#1D4ED8]/5" : "bg-background",
            disabled ? "opacity-70" : ""].join(" ")}>
            <div className="flex items-start gap-2.5">
                <Icon size={14} className={value ? "mt-0.5 text-[#1D4ED8]" : "mt-0.5 text-muted-foreground"} />
                <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-semibold">{label}</div>
                        <Switch value={value} onChange={onChange} disabled={disabled} testId={testId} />
                    </div>
                    {hint && <div className="mt-1 text-[11px] leading-snug text-muted-foreground">{hint}</div>}
                </div>
            </div>
        </div>
    );
}

function Switch({ value, onChange, disabled, testId }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={value}
            onClick={() => !disabled && onChange(!value)}
            data-testid={testId}
            disabled={disabled}
            className={[
                "relative inline-flex h-5 w-9 items-center rounded-full border border-border transition-colors",
                value ? "bg-[#1D4ED8]" : "bg-muted",
                disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer",
            ].join(" ")}
        >
            <span
                className={[
                    "inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform",
                    value ? "translate-x-4" : "translate-x-0.5",
                ].join(" ")}
            />
        </button>
    );
}

function InputField({ label, value, onChange, placeholder, disabled, testId }) {
    return (
        <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {label}
            </span>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                placeholder={placeholder}
                data-testid={testId}
                className="w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-[11px] text-foreground placeholder:text-muted-foreground/50 focus:border-[#1D4ED8] focus:outline-none focus:ring-1 focus:ring-[#1D4ED8]/30 disabled:opacity-60"
            />
        </label>
    );
}

function LogRow({ entry }) {
    return (
        <div className="px-4 py-3 text-xs hover:bg-muted/30" data-testid={`notif-log-${entry.id}`}>
            <div className="flex items-center justify-between gap-2">
                <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                    {entry.channel}
                </span>
                {entry.ok ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#10b981]">
                        <CheckCircle2 size={11} /> delivered
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#ef4444]">
                        <XCircle size={11} /> failed
                    </span>
                )}
            </div>
            <div className="mt-1.5 font-medium text-foreground">{entry.subject}</div>
            {entry.target && (
                <div className="mt-0.5 font-mono text-[10px] text-muted-foreground break-all">{entry.target}</div>
            )}
            {entry.detail && (
                <div className="mt-0.5 font-mono text-[10px] text-muted-foreground break-all line-clamp-2">{entry.detail}</div>
            )}
            <div className="mt-1 font-mono text-[10px] text-muted-foreground/70">
                {(entry.created_at || "").replace("T", " ").slice(0, 19)}
            </div>
        </div>
    );
}

function timeAgo(iso) {
    if (!iso) return "—";
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t) / 1000;
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
}
